import { Injectable } from "@angular/core";
import { GlobalService } from "app/global/global.service";
import { BasePermissionService } from "app/global/permissions/base-permission-service";
import { PermissionsService } from "app/global/permissions/permissions.service";
import { UserSettings, Workspace } from "app/global/user-settings.dto";
import { UserSettingsFacade } from "app/store/user-settings.facade";
import { map, Observable } from "rxjs";
import { AccessLevelEnum, AccessLevelPriority } from "../users/roles/roles.service";
import { CategoryShareModeEnum } from "../categories.dto";
import { checkCategoryOverDocType } from "app/store/selectors/roles.selectors";

export interface IUserResultT {
    user_id: number
    workspace_id: number
}

@Injectable({
    providedIn: 'root'
})


export class ContractControlPermissionService extends BasePermissionService {

    public isTenantOwner$ = this._permissionServce.isTenantOwner$;
    public defaultWorkSpace$ = this._globalService.getUserDefaultWorkSpace();
    public userSettings$ = this._userSettingsFacade$._userSettings$;

    constructor(
        private _permissionServce: PermissionsService,
        private _userSettingsFacade$: UserSettingsFacade,
        private _globalService: GlobalService
    ) {
        super(
            _permissionServce,
            _permissionServce.isTenantOwner$,
            _globalService.getUserDefaultWorkSpace(),
            _userSettingsFacade$._userSettings$
        );
    }



    /**
     * Retrieves the access level for a given contract ID.
     *
     * @param {number} id - The ID of the contract.
     * @param docTypeId - The ID of the document type.
     * @param categoryId - The ID of the category.
     * @returns {Observable<AccessLevelEnum>} An observable that emits the access level of the contract.
     */

    
    protected getAccessLevel(id: number, docTypeId:number, categoryId:number | number[]): Observable<AccessLevelEnum> {
        return this._permissionServce.contractsAccessLevel(id, docTypeId, categoryId);
    }



    /**
     * Determines if the user has permission to edit a contract.
     *
     * @template T - A type that extends an object containing `user_id` and `workspace_id` properties.
     * @param {number} contractTypeId - The ID of the contract type.
     * @param {T} result - An object containing the user's ID and workspace ID.
     * @returns {Observable<boolean>} An observable that emits `true` if the user has edit permissions, otherwise `false`.
     */
    canEditContract<T extends { user_id: number, workspace_id: number }>(contractTypeId: number,  docTypeId:number, categoryId:number | number[], result: T): Observable<boolean> {
        return this.verifyPermission(contractTypeId, docTypeId, categoryId, result).pipe(
            map(accessLevel => [AccessLevelEnum.edit, AccessLevelEnum.use_and_edit, AccessLevelEnum.full_access].includes(accessLevel))
        );
    }




    /**
     * Checking if the user has full access
     * @param contractTypeId 
     * @param result 
     * @returns Observable<boolean>
     */
    hasFullAccess<T extends { user_id: number, workspace_id: number }>(contractTypeId: number, docTypeId:number, categoryId:number | number[], result: T): Observable<boolean> {
        return this.verifyPermission(contractTypeId, docTypeId, categoryId, result).pipe(
            map(accessLevel => accessLevel === AccessLevelEnum.full_access)
        );
    }





    /**
     * Checks and returns the appropriate access level based on the provided parameters.
     *
     * @param {boolean} hasFullAccess - Indicates if the user has full access.
     * @param {AccessLevelEnum} accessLevel - The current access level of the user.
     * 
     * @private
     * @returns {AccessLevelEnum} - Returns `AccessLevelEnum.full_access` if `hasFullAccess` is true,
     * otherwise returns the provided `accessLevel`. If `accessLevel` is not provided, returns `null`.
     */
    protected checkRolesAccessLevel(hasFullAccess: boolean, accessLevel: AccessLevelEnum): AccessLevelEnum {
        return hasFullAccess ? AccessLevelEnum.full_access : accessLevel ? accessLevel : null;
    }



    /**
     * Checks if the user has access to a specific contract type within a workspace.
     *
     * @param userSettings$ - The settings of the user, including their ID and groups.
     * @param result - An object containing the user ID and workspace ID.
     * @param defaultWorkSpace$ - The default workspace of the user.
     * @param contractTypeId - The ID of the contract type to check access for.
     * @param accessLevel - The desired access level to check for.
     * 
     * @private
     * @returns A AccessLevelEnum indicating whether the user has access to the contract type, or null if no access.
     */
    protected checkGroupAccess(
        userSettings$: UserSettings,
        result: IUserResultT,
        defaultWorkSpace$: Workspace,
        contractTypeId: number,
        docTypeId: number,
        categoryIds: number | number[],
        parentCategoryIds: number[]
    ): AccessLevelEnum {
        const highestAccessLevel: AccessLevelEnum | null = null;

        if (userSettings$.id === result.user_id) {
            return AccessLevelEnum.full_access;
        }
        if (defaultWorkSpace$.id !== result.workspace_id) {
            return highestAccessLevel;
        }
        return this.getHighestAccessLevel(userSettings$, contractTypeId, categoryIds, parentCategoryIds);
    }



    /**
     * Determines the highest access level a user has for a specific contract type.
     *
     * @param userSettings$ - The settings of the user, including their groups and access levels.
     * @param contractTypeId - The ID of the contract type to check access for.
     * @param accessLevel - The required access level to validate against.
     * @returns The highest access level the user has for the specified contract type.
     */
    private getHighestAccessLevel(userSettings$: UserSettings, contractTypeId: number, categoryIds: number | number[], parentCategoryIds: number[]): AccessLevelEnum {
        const accessLevelPriority = AccessLevelPriority;

        const appliedAccessLevels: AccessLevelEnum[] = [];

        for (const group of userSettings$.groups) {
            if (!group.contract_types?.length) continue;

            // Check if the user is in the group
            const groupUser = group.group_users.find(x => x.user_id === userSettings$.id);
            if (!groupUser) continue; // Skip if user is not in this group

            // Check if the contract type and category is in the group
            const isContractTypesContains = group.contract_types.some(x => x.document_type_id === contractTypeId && checkCategoryOverDocType(x, categoryIds, parentCategoryIds));

            // Validate access based on the required level
            if (
                [AccessLevelEnum.full_access, AccessLevelEnum.edit, AccessLevelEnum.use_only].includes(groupUser.contracts_access_level) &&
                isContractTypesContains &&
                ( groupUser)
            ) {
                appliedAccessLevels.push(groupUser.contracts_access_level);
            }
        }

        // Determine the highest priority access level
        return appliedAccessLevels.sort((a, b) => accessLevelPriority.indexOf(a) - accessLevelPriority.indexOf(b))[0] || null;
    }
}
